import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { ruleReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, CountChip, Pager, TabBar, WarnBanner, Dash,
  severityTone, errMessage, DEFAULT_PAGE_SIZE,
} from './ReportCentreKit';
import type {
  UnusualJournalsFilter, UnusualJournalsDto,
  SodExceptionsFilter, SodExceptionsDto,
  ReportRule, UnusualJournalRow, SodExceptionRow,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'UNUSUAL_JOURNALS';

const SEVERITY_OPTIONS = [
  { value: '',       label: 'All Severities' },
  { value: 'LOW',    label: 'Low and above' },
  { value: 'MEDIUM', label: 'Medium and above' },
  { value: 'HIGH',   label: 'High only' },
];

const EDIT_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'];

// ── Export column sets ────────────────────────────────────────────────────────

const UNUSUAL_COLUMNS: ExcelColumn[] = [
  { key: 'Journal',   header: 'Journal No',   width: 18 },
  { key: 'DocDate',   header: 'Doc Date',     width: 14 },
  { key: 'PostDate',  header: 'Posting Date', width: 14 },
  { key: 'Type',      header: 'Type',         width: 10 },
  { key: 'Debit',     header: 'Total Debit',  width: 16, numFmt: '#,##0.00' },
  { key: 'Rule',      header: 'Rule',         width: 26 },
  { key: 'Severity',  header: 'Severity',     width: 10 },
  { key: 'Detail',    header: 'Detail',       width: 46 },
  { key: 'Accounts',  header: 'Accounts',     width: 26 },
  { key: 'CreatedBy', header: 'Created By',   width: 20 },
  { key: 'ApprovedBy', header: 'Approved By', width: 20 },
  { key: 'Status',    header: 'Workflow',     width: 14 },
];

function buildUnusualRows(rows: UnusualJournalRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Journal:    r.documentNumber,
    DocDate:    r.documentDate,
    PostDate:   r.postingDate,
    Type:       r.transactionTypeCode,
    Debit:      r.totalDebit,
    Rule:       `${r.ruleCode} — ${r.ruleName}`,
    Severity:   r.severity,
    Detail:     r.detail,
    Accounts:   r.accountsAffected,
    CreatedBy:  r.createdByName ?? '',
    ApprovedBy: r.approvedByName ?? '',
    Status:     r.workflowStatus,
  }));
}

const SOD_COLUMNS: ExcelColumn[] = [
  { key: 'Journal',   header: 'Journal No',          width: 18 },
  { key: 'PostDate',  header: 'Posting Date',        width: 14 },
  { key: 'Debit',     header: 'Total Debit',         width: 16, numFmt: '#,##0.00' },
  { key: 'Rule',      header: 'Rule',                width: 26 },
  { key: 'Severity',  header: 'Severity',            width: 10 },
  { key: 'User',      header: 'User',                width: 22 },
  { key: 'Conflict',  header: 'Conflicting Actions', width: 24 },
  { key: 'Dates',     header: 'Action Dates',        width: 36 },
  { key: 'Detail',    header: 'Detail',              width: 46 },
];

function buildSodRows(rows: SodExceptionRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Journal:  r.documentNumber,
    PostDate: r.postingDate,
    Debit:    r.totalDebit,
    Rule:     `${r.ruleCode} — ${r.ruleName}`,
    Severity: r.severity,
    User:     r.userName,
    Conflict: r.conflictingActions,
    Dates:    r.actionDates,
    Detail:   r.detail,
  }));
}

// ── Hits-by-rule chips ────────────────────────────────────────────────────────

const HitsByRule = ({ hits, rules }: { hits: Record<string, number>; rules: ReportRule[] }) => {
  const entries = Object.entries(hits);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {entries.map(([code, count]) => {
        const rule = rules.find(r => r.code === code);
        return (
          <CountChip
            key={code}
            label={rule ? rule.name : code}
            count={count}
            tone={rule ? severityTone(rule.severity) : 'grey'}
          />
        );
      })}
    </div>
  );
};

// ── Rule editor row (Rules tab) ───────────────────────────────────────────────

interface RuleEditorProps {
  rule: ReportRule;
  onSaved: (updated: ReportRule) => void;
  onCancel: () => void;
}

const RuleEditor = ({ rule, onSaved, onCancel }: RuleEditorProps) => {
  const [severity, setSeverity] = useState(rule.severity);
  const [paramsJson, setParamsJson] = useState(rule.paramsJson ?? '');
  const [isEnabled, setIsEnabled] = useState(rule.isEnabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const jsonError = useMemo(() => {
    const trimmed = paramsJson.trim();
    if (trimmed === '') return null;
    try {
      JSON.parse(trimmed);
      return null;
    } catch (e: unknown) {
      return e instanceof Error ? e.message : 'Invalid JSON';
    }
  }, [paramsJson]);

  const save = async () => {
    if (jsonError) return;
    setSaving(true);
    setError(null);
    try {
      const trimmed = paramsJson.trim();
      const res = await ruleReportApi.updateRule(rule.id, {
        severity,
        paramsJson: trimmed === '' ? null : trimmed,
        isEnabled,
      });
      const updated = res.data?.data;
      onSaved(updated ?? { ...rule, severity, paramsJson: trimmed === '' ? null : trimmed, isEnabled, isCompanyOverride: true });
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-40">
          <label className={LABEL_CLS}>Severity</label>
          <select value={severity} onChange={e => setSeverity(e.target.value)} className={INPUT_CLS}>
            {EDIT_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer pb-2">
          <input type="checkbox" checked={isEnabled} onChange={e => setIsEnabled(e.target.checked)} className="rounded" />
          Enabled
        </label>
      </div>
      <div>
        <label className={LABEL_CLS}>Parameters (JSON)</label>
        <textarea
          value={paramsJson}
          onChange={e => setParamsJson(e.target.value)}
          rows={4}
          spellCheck={false}
          placeholder='e.g. {"thresholdAmount": 1000000}'
          className={cn(INPUT_CLS, 'font-mono text-xs', jsonError && 'border-red-400 dark:border-red-600')}
        />
        {jsonError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Invalid JSON: {jsonError}</p>}
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { void save(); }}
          disabled={saving || !!jsonError}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Cancel
        </button>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Saving a platform default creates a company override; the change is audit-logged.
        </span>
      </div>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

type Tab = 'unusual' | 'sod' | 'rules';

export const UnusualJournalsPage = () => {
  const { hasPermission } = usePermission();
  const permitted = hasPermission(Permissions.RPT_USER_ACTIVITY);

  const [tab, setTab] = useState<Tab>('unusual');

  // Rules — loaded once (both types); feeds the rule-code selects and the Rules tab.
  const [rules, setRules] = useState<ReportRule[] | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  // Unusual tab
  const [unFilter, setUnFilter] = useState<UnusualJournalsFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [unApplied, setUnApplied] = useState<UnusualJournalsFilter | null>(null);
  const [unData, setUnData] = useState<UnusualJournalsDto | null>(null);
  const [unLoading, setUnLoading] = useState(false);
  const [unError, setUnError] = useState<string | null>(null);

  // SoD tab
  const [sodFilter, setSodFilter] = useState<SodExceptionsFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sodApplied, setSodApplied] = useState<SodExceptionsFilter | null>(null);
  const [sodData, setSodData] = useState<SodExceptionsDto | null>(null);
  const [sodLoading, setSodLoading] = useState(false);
  const [sodError, setSodError] = useState<string | null>(null);

  useEffect(() => {
    if (!permitted) return;
    let cancelled = false;
    setRulesLoading(true);
    setRulesError(null);
    ruleReportApi.getRules()
      .then(res => { if (!cancelled) setRules(res.data?.data ?? []); })
      .catch((e: unknown) => { if (!cancelled) setRulesError(errMessage(e)); })
      .finally(() => { if (!cancelled) setRulesLoading(false); });
    return () => { cancelled = true; };
  }, [permitted]);

  const unusualRules = useMemo(() => (rules ?? []).filter(r => r.ruleType === 'UNUSUAL'), [rules]);
  const sodRules     = useMemo(() => (rules ?? []).filter(r => r.ruleType === 'SOD'), [rules]);

  const fetchUnusual = useCallback(async (base: UnusualJournalsFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setUnLoading(true);
    setUnError(null);
    try {
      const res = await ruleReportApi.getUnusualJournals(query);
      setUnData(res.data?.data ?? null);
      setUnApplied(query);
    } catch (e: unknown) {
      setUnError(errMessage(e));
    } finally {
      setUnLoading(false);
    }
  }, []);

  const fetchSod = useCallback(async (base: SodExceptionsFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setSodLoading(true);
    setSodError(null);
    try {
      const res = await ruleReportApi.getSodExceptions(query);
      setSodData(res.data?.data ?? null);
      setSodApplied(query);
    } catch (e: unknown) {
      setSodError(errMessage(e));
    } finally {
      setSodLoading(false);
    }
  }, []);

  if (!permitted) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const unPage = unData?.rows;
  const unItems = unPage?.items ?? [];
  const sodPage = sodData?.rows;
  const sodItems = sodPage?.items ?? [];

  const hasData = tab === 'unusual' ? !!unData : tab === 'sod' ? !!sodData : (rules?.length ?? 0) > 0;
  const rowCount = tab === 'unusual' ? unItems.length : tab === 'sod' ? sodItems.length : (rules?.length ?? 0);

  const handleExcel = () => {
    if (tab === 'unusual' && unData && unPage) {
      exportToExcel({
        sheetName: 'Unusual Journals',
        columns: UNUSUAL_COLUMNS,
        rows: buildUnusualRows(unItems),
        title: 'Unusual Journal Listing',
        subtitle: `${unData.fromDate} to ${unData.toDate} · Page ${unPage.pageNumber} of ${unPage.totalPages}`,
      }, `UnusualJournals_${unData.fromDate}_${unData.toDate}_p${unPage.pageNumber}`);
    } else if (tab === 'sod' && sodData && sodPage) {
      exportToExcel({
        sheetName: 'SoD Exceptions',
        columns: SOD_COLUMNS,
        rows: buildSodRows(sodItems),
        title: 'Segregation of Duties Exceptions',
        subtitle: `${sodData.fromDate} to ${sodData.toDate} · Page ${sodPage.pageNumber} of ${sodPage.totalPages}`,
      }, `SodExceptions_${sodData.fromDate}_${sodData.toDate}_p${sodPage.pageNumber}`);
    }
  };

  const handleCsv = () => {
    if (tab === 'unusual' && unData && unPage) {
      exportToCsvRows(UNUSUAL_COLUMNS, buildUnusualRows(unItems), `UnusualJournals_${unData.fromDate}_${unData.toDate}_p${unPage.pageNumber}`);
    } else if (tab === 'sod' && sodData && sodPage) {
      exportToCsvRows(SOD_COLUMNS, buildSodRows(sodItems), `SodExceptions_${sodData.fromDate}_${sodData.toDate}_p${sodPage.pageNumber}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Search size={22} className="text-brand-500" />
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Unusual Journals</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Exceptions requiring review — a listed journal is not an accusation.
            </p>
          </div>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={hasData && tab !== 'rules'}
          reportKey={REPORT_KEY}
          rowCount={rowCount}
          filterSummary={tab === 'unusual'
            ? `${unFilter.fromDate}..${unFilter.toDate}`
            : `${sodFilter.fromDate}..${sodFilter.toDate}`}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'unusual', label: 'Unusual Journals' },
          { key: 'sod', label: 'Segregation of Duties' },
          { key: 'rules', label: 'Rules' },
        ]}
        active={tab}
        onChange={k => setTab(k as Tab)}
      />

      {/* ── Unusual tab ──────────────────────────────────────────────────────── */}
      {tab === 'unusual' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-44">
                  <label className={LABEL_CLS}>From Date *</label>
                  <input type="date" value={unFilter.fromDate} onChange={e => setUnFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>To Date *</label>
                  <input type="date" value={unFilter.toDate} onChange={e => setUnFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>Min Severity</label>
                  <select
                    value={unFilter.minSeverity ?? ''}
                    onChange={e => setUnFilter(f => ({ ...f, minSeverity: e.target.value || undefined }))}
                    className={INPUT_CLS}
                  >
                    {SEVERITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="w-56">
                  <label className={LABEL_CLS}>Rule</label>
                  <select
                    value={unFilter.ruleCode ?? ''}
                    onChange={e => setUnFilter(f => ({ ...f, ruleCode: e.target.value || undefined }))}
                    className={INPUT_CLS}
                    disabled={rulesLoading}
                  >
                    <option value="">All Rules</option>
                    {unusualRules.map(r => <option key={r.id} value={r.code}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <RunReportButton
                onClick={() => { void fetchUnusual(unFilter, 1); }}
                isLoading={unLoading}
                disabled={!unFilter.fromDate || !unFilter.toDate}
              />
            </div>
          </div>

          {unError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {unError}
            </div>
          )}

          {unLoading && !unData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!unData && !unLoading && (
            <EmptyState message='Set the date range and click "Run Report" to scan for journals matching the unusual-activity rules.' />
          )}

          {unData && unPage && (
            <div className="flex flex-col gap-4">
              {unData.scanTruncated && (
                <WarnBanner>
                  The scan window hit its cap after {unData.journalsScanned.toLocaleString('en-US')} journals —
                  results may be incomplete. Narrow the date range for full coverage.
                </WarnBanner>
              )}

              <HitsByRule hits={unData.hitsByRule} rules={unData.rulesEvaluated} />

              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Exceptions Requiring Review</h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {fmtDate(unData.fromDate)} to {fmtDate(unData.toDate)}
                    {' · '}{unData.journalsScanned.toLocaleString('en-US')} journals scanned
                  </span>
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[1500px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={cn(TH_CLS, 'w-40')}>Journal No</th>
                        <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                        <th className={cn(TH_CLS, 'w-20')}>Type</th>
                        <th className={TH_R_CLS}>Total Debit</th>
                        <th className={TH_CLS}>Rule</th>
                        <th className={cn(TH_CLS, 'w-24')}>Severity</th>
                        <th className={TH_CLS}>Detail</th>
                        <th className={TH_CLS}>Accounts</th>
                        <th className={cn(TH_CLS, 'w-36')}>Created By</th>
                        <th className={cn(TH_CLS, 'w-36')}>Approved By</th>
                        <th className={cn(TH_CLS, 'w-28')}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unItems.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No rule hits in the selected window.
                          </td>
                        </tr>
                      ) : unItems.map((r, i) => (
                        <tr key={`${r.journalId}-${r.ruleCode}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.documentNumber}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.transactionTypeCode}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500 block">{r.ruleCode}</span>
                            {r.ruleName}
                          </td>
                          <td className="px-3 py-2"><Pill label={r.severity} tone={severityTone(r.severity)} /></td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.detail}</td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.accountsAffected}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.createdByName ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.approvedByName ?? <Dash />}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.workflowStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  pageNumber={unPage.pageNumber}
                  totalPages={unPage.totalPages}
                  totalCount={unPage.totalCount}
                  shownCount={unItems.length}
                  loading={unLoading}
                  onPage={p => { void fetchUnusual(unApplied ?? unFilter, p); }}
                  noun="exceptions"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── SoD tab ──────────────────────────────────────────────────────────── */}
      {tab === 'sod' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-44">
                  <label className={LABEL_CLS}>From Date *</label>
                  <input type="date" value={sodFilter.fromDate} onChange={e => setSodFilter(f => ({ ...f, fromDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>To Date *</label>
                  <input type="date" value={sodFilter.toDate} onChange={e => setSodFilter(f => ({ ...f, toDate: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div className="w-56">
                  <label className={LABEL_CLS}>Rule</label>
                  <select
                    value={sodFilter.ruleCode ?? ''}
                    onChange={e => setSodFilter(f => ({ ...f, ruleCode: e.target.value || undefined }))}
                    className={INPUT_CLS}
                    disabled={rulesLoading}
                  >
                    <option value="">All Rules</option>
                    {sodRules.map(r => <option key={r.id} value={r.code}>{r.name}</option>)}
                  </select>
                </div>
              </div>
              <RunReportButton
                onClick={() => { void fetchSod(sodFilter, 1); }}
                isLoading={sodLoading}
                disabled={!sodFilter.fromDate || !sodFilter.toDate}
              />
            </div>
          </div>

          {sodError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {sodError}
            </div>
          )}

          {sodLoading && !sodData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!sodData && !sodLoading && (
            <EmptyState message='Set the date range and click "Run Report" to find journals where one user held conflicting duties.' />
          )}

          {sodData && sodPage && (
            <div className="flex flex-col gap-4">
              <HitsByRule hits={sodData.hitsByRule} rules={sodData.rulesEvaluated} />

              <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Exceptions Requiring Review</h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {fmtDate(sodData.fromDate)} to {fmtDate(sodData.toDate)}
                  </span>
                </div>
                <div className="overflow-auto">
                  <table className="w-full text-sm border-collapse min-w-[1400px]">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                      <tr>
                        <th className={cn(TH_CLS, 'w-40')}>Journal No</th>
                        <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                        <th className={TH_R_CLS}>Total Debit</th>
                        <th className={TH_CLS}>Rule</th>
                        <th className={cn(TH_CLS, 'w-24')}>Severity</th>
                        <th className={cn(TH_CLS, 'w-40')}>User</th>
                        <th className={TH_CLS}>Conflicting Actions</th>
                        <th className={TH_CLS}>Action Dates</th>
                        <th className={TH_CLS}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sodItems.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                            No segregation-of-duties exceptions in the selected window.
                          </td>
                        </tr>
                      ) : sodItems.map((r, i) => (
                        <tr key={`${r.journalId}-${r.ruleCode}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.documentNumber}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
                          <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500 block">{r.ruleCode}</span>
                            {r.ruleName}
                          </td>
                          <td className="px-3 py-2"><Pill label={r.severity} tone={severityTone(r.severity)} /></td>
                          <td className="px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-200">{r.userName}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.conflictingActions}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.actionDates}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager
                  pageNumber={sodPage.pageNumber}
                  totalPages={sodPage.totalPages}
                  totalCount={sodPage.totalCount}
                  shownCount={sodItems.length}
                  loading={sodLoading}
                  onPage={p => { void fetchSod(sodApplied ?? sodFilter, p); }}
                  noun="exceptions"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Rules tab ────────────────────────────────────────────────────────── */}
      {tab === 'rules' && (
        <>
          {rulesError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {rulesError}
            </div>
          )}

          {rulesLoading && !rules && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {rules && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Detection Rules</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Rules are configuration, not code. Every edit is audit-logged.
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{rules.length} rules</span>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1100px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={cn(TH_CLS, 'w-24')}>Type</th>
                      <th className={cn(TH_CLS, 'w-44')}>Code</th>
                      <th className={TH_CLS}>Name</th>
                      <th className={cn(TH_CLS, 'w-24')}>Severity</th>
                      <th className={cn(TH_CLS, 'w-24')}>Enabled</th>
                      <th className={cn(TH_CLS, 'w-32')}>Scope</th>
                      <th className={cn(TH_CLS, 'w-24')}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No detection rules configured.
                        </td>
                      </tr>
                    ) : rules.map(r => (
                      <Fragment key={r.id}>
                        <tr className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-3 py-2">
                            <Pill label={r.ruleType} tone={r.ruleType === 'SOD' ? 'purple' : 'blue'} />
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.code}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {r.name}
                            {r.description && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{r.description}</span>
                            )}
                          </td>
                          <td className="px-3 py-2"><Pill label={r.severity} tone={severityTone(r.severity)} /></td>
                          <td className="px-3 py-2">
                            <Pill label={r.isEnabled ? 'ON' : 'OFF'} tone={r.isEnabled ? 'green' : 'grey'} />
                          </td>
                          <td className="px-3 py-2">
                            {r.isCompanyOverride
                              ? <Pill label="COMPANY OVERRIDE" tone="amber" title="This company has customised the platform default" />
                              : <Pill label="PLATFORM" tone="grey" />}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => setEditingRuleId(id => (id === r.id ? null : r.id))}
                              className="px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              {editingRuleId === r.id ? 'Close' : 'Edit'}
                            </button>
                          </td>
                        </tr>
                        {editingRuleId === r.id && (
                          <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
                            <td colSpan={7} className="px-6 py-4">
                              <RuleEditor
                                rule={r}
                                onSaved={updated => {
                                  setRules(prev => (prev ?? []).map(x => (x.id === r.id ? updated : x)));
                                  setEditingRuleId(null);
                                }}
                                onCancel={() => setEditingRuleId(null)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
